import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Shield, CheckCircle, ArrowLeft, FileText, Users, Lock } from 'lucide-react';

const UnclaimedFunds = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Breadcrumb */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 text-gray-400">
            <Link to="/solutions" className="hover:text-bluemp-400 transition-colors">Solutions</Link>
            <span>/</span>
            <span className="text-white">Unclaimed / Unpaid AI Solution</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/solutions" className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors mb-6">
                <ArrowLeft size={16} />
                Back to Solutions
              </Link>
              
              <h1 className="display-large text-white mb-6">
                Intelligent Tracing with{' '}
                <span className="text-bluemp-400">Integrity</span>
              </h1>
              
              <p className="body-large text-gray-300 mb-8">
                Responsible tracing powered by intelligent data matching for ethical and efficient beneficiary identification. Help organizations manage unclaimed and unpaid funds while maintaining the highest standards of compliance and transparency.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  Implement Ethical Tracing
                  <ArrowRight size={20} />
                </Link>
                <Link to="/experience" className="btn-secondary">
                  View Compliance Results
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <Search size={64} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4">Ethical Tracing Platform</h3>
                <p className="body-medium text-gray-300 mb-6">
                  Advanced AI matching algorithms combined with comprehensive compliance frameworks ensure responsible and effective beneficiary identification.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">100% ethical compliance</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Transparent audit trails</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Secure processing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Core Capabilities
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Comprehensive tracing capabilities that prioritize ethical practices, regulatory compliance, and transparent processes throughout the entire beneficiary identification journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Search,
                title: "AI-Driven Data Matching & Enrichment",
                description: "Advanced algorithms intelligently match and enrich beneficiary data while maintaining strict privacy and ethical standards."
              },
              {
                icon: Users,
                title: "Beneficiary Tracing & Validation", 
                description: "Comprehensive tracing processes that identify and validate beneficiaries through multiple verified data sources and methodologies."
              },
              {
                icon: FileText,
                title: "Compliance Audit Trails",
                description: "Complete audit trail documentation ensuring full transparency and compliance with all regulatory requirements and ethical guidelines."
              },
              {
                icon: Lock,
                title: "Secure Data Processing",
                description: "Bank-level security protocols protect sensitive information throughout the entire tracing and identification process."
              }
            ].map((capability, index) => (
              <div key={index} className="group dark-hover bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <capability.icon size={48} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {capability.title}
                </h3>
                <p className="body-medium text-gray-300">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Implement Ethical Unclaimed Fund Tracing?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Transform your unclaimed fund management with AI-powered tracing that maintains the highest ethical standards and regulatory compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Start Ethical Tracing
              <ArrowRight size={20} />
            </Link>
            <Link to="/ai-learning" className="btn-secondary">
              Explore AI Learning
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UnclaimedFunds;